import React, { useState } from 'react';
import styled from 'styled-components';
import AddCourseMaterialsModal from './AddCourseMaterialsModal';

/**
 * The "Add course materials" card in Create Task → Advanced (per the approved design).
 *
 * Materials attach at TASK level: the card lists what is attached and opens the
 * Upload/Write modal. Selection of syllabus outcomes is no longer a teacher job — the
 * backend auto-selects the relevant catalog outcomes at marking time — so this card is
 * the entire course-materials surface.
 */
// Mirrors the server's task-wide cap (CourseMaterialSelectionValidator) so a teacher
// never creates materials the save is then forced to reject.
export const MAX_MATERIALS_PER_TASK = 8;

export const TaskCourseMaterialsCard = ({
  materials = [],
  onAdd,
  onRemove,
  onSaveStarted,
  onSaveSettled,
  canAttach = true,
  isReadOnly = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const atCap = materials.length >= MAX_MATERIALS_PER_TASK;

  return (
    <Card>
      <CardTitle>📄 Add course materials</CardTitle>
      <CardHint>
        Prioritise specific teaching and learning content when generating feedback for this
        task
      </CardHint>

      {materials.length > 0 && (
        <MaterialList>
          {materials.map((material) => (
            <MaterialRow key={material.id}>
              <MaterialName>{material.title || 'Course materials'}</MaterialName>
              {!isReadOnly && (
                <RemoveButton
                  type="button"
                  aria-label={`Remove ${material.title || 'course material'}`}
                  onClick={() => onRemove?.(material.id)}
                >
                  ✕
                </RemoveButton>
              )}
            </MaterialRow>
          ))}
        </MaterialList>
      )}

      {!isReadOnly &&
        (canAttach && atCap ? (
          <CardHint>A task can carry at most {MAX_MATERIALS_PER_TASK} attached materials.</CardHint>
        ) : canAttach ? (
          <AddLink type="button" onClick={() => setIsModalOpen(true)}>
            + Add materials
          </AddLink>
        ) : materials.length > 0 ? (
          // The refs persist on the task's questions, so with every question deleted a
          // save cannot keep these. Say so instead of losing them silently.
          <WarningHint>
            These materials need at least one question — add a question back before saving,
            or they won't be kept.
          </WarningHint>
        ) : (
          <CardHint>Add a question first — course materials save with the task's questions.</CardHint>
        ))}

      {isModalOpen && (
        <AddCourseMaterialsModal
          onClose={() => setIsModalOpen(false)}
          onSaved={(material) => onAdd?.({ id: material.id, title: material.title })}
          onSaveStarted={onSaveStarted}
          onSaveSettled={onSaveSettled}
          remainingCapacity={MAX_MATERIALS_PER_TASK - materials.length}
        />
      )}
    </Card>
  );
};

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid var(--color-neutral-alpha-90, #c9c6cc80);
  border-radius: 12px;
  padding: 16px;
  background: #ffffff;
  font-family: var(--font-family-ibm_plex_sans);
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--text, #1d1929);
`;

const CardHint = styled.div`
  font-size: 13px;
  color: #6f6a76;
`;

const WarningHint = styled.div`
  font-size: 13px;
  color: #b3261e;
`;

const MaterialList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 6px 0 2px;
`;

const MaterialRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border: 1px solid #eceaee;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--text, #1d1929);
`;

const MaterialName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  border: none;
  background: none;
  color: #6f6a76;
  cursor: pointer;
  font-size: 13px;
  padding: 2px 4px;
  flex-shrink: 0;

  &:hover {
    color: #c62828;
  }
`;

const AddLink = styled.button`
  align-self: flex-start;
  border: none;
  background: none;
  color: #7200e0;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 0 0;
  cursor: pointer;
`;

export default TaskCourseMaterialsCard;
